/**
 * Responds only after the configured native Klaviyo embed emits its successful
 * submit event. Form rendering and lead capture stay with Klaviyo; campaign
 * navigation is optional per landing-page section.
 */
(() => {
  if (customElements.get('wefunder-landing')) return;

  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

  class WefunderLanding extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.embed = this.querySelector('[data-klaviyo-embed]');
      this.success = this.querySelector('[data-success]');
      this.onKlaviyoFormEvent = this.onKlaviyoFormEvent.bind(this);
      this.onEmbedClick = this.onEmbedClick.bind(this);
      window.addEventListener('klaviyoForms', this.onKlaviyoFormEvent);
      this.addEventListener('click', this.onEmbedClick, true);
      this.initialized = true;
    }

    disconnectedCallback() {
      window.removeEventListener('klaviyoForms', this.onKlaviyoFormEvent);
      this.removeEventListener('click', this.onEmbedClick, true);
      this.clearPendingCampaignWindow(true);
      this.initialized = false;
    }

    onEmbedClick(event) {
      if (this.dataset.openCampaignOnSubmit !== 'true' || !this.embed) return;

      const button = event.target.closest?.('button');
      if (!button || !this.embed.contains(button)) return;

      const email = this.embed.querySelector('input[type="email"]');
      if (!email?.value.trim() || email.validity?.typeMismatch) return;
      if (this.pendingCampaignWindow && !this.pendingCampaignWindow.closed) return;

      const campaignWindow = window.open('about:blank', '_blank');
      if (!campaignWindow) return;

      try {
        campaignWindow.opener = null;
      } catch (error) {
        // The new tab is still safe to navigate even if the browser owns opener handling.
      }

      this.pendingCampaignWindow = campaignWindow;
      this.pendingCampaignTimeout = window.setTimeout(() => {
        this.clearPendingCampaignWindow(true);
      }, 30000);
    }

    onKlaviyoFormEvent(event) {
      const detail = event.detail || {};
      if (detail.type !== 'submit' || detail.formId !== this.dataset.klaviyoFormId) return;
      if (!this.embed || !this.success) return;

      try {
        this.identifyLead(detail.metaData || {});
      } catch (error) {
        console.error('[wefunder-landing] Klaviyo attribution failed.', error);
      }
      this.openCampaignAfterSubmit();
      this.embed.hidden = true;
      this.success.hidden = false;
      this.success.focus({ preventScroll: true });
      this.success.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }

    openCampaignAfterSubmit() {
      if (this.dataset.openCampaignOnSubmit !== 'true' || !this.dataset.wefunderUrl) return;

      const campaignWindow = this.pendingCampaignWindow;
      this.clearPendingCampaignWindow(false);

      if (campaignWindow && !campaignWindow.closed) {
        try {
          campaignWindow.location.replace(this.dataset.wefunderUrl);
          return;
        } catch (error) {
          // Fall through to a fresh tab if the prepared tab can no longer be navigated.
        }
      }

      window.open(this.dataset.wefunderUrl, '_blank', 'noopener');
    }

    clearPendingCampaignWindow(closeWindow) {
      if (this.pendingCampaignTimeout) {
        window.clearTimeout(this.pendingCampaignTimeout);
      }

      const campaignWindow = this.pendingCampaignWindow;
      this.pendingCampaignTimeout = null;
      this.pendingCampaignWindow = null;

      if (closeWindow && campaignWindow && !campaignWindow.closed) {
        campaignWindow.close();
      }
    }

    identifyLead(metadata) {
      const email = metadata.email || metadata.$email;
      if (!email) return;

      const identity = { email };
      const configuredProperties = {
        event: this.dataset.trackingEvent,
        lead_type: this.dataset.leadType,
        source: this.dataset.source,
      };
      Object.entries(configuredProperties).forEach(([key, value]) => {
        if (value) identity[key] = value;
      });

      if (this.dataset.preserveUtm === 'true') {
        const searchParams = new URLSearchParams(window.location.search);
        UTM_KEYS.forEach((key) => {
          const value = searchParams.get(key)?.trim();
          if (value) identity[key] = value.slice(0, 255);
        });
      }

      if (typeof window.klaviyo?.identify === 'function') {
        Promise.resolve(window.klaviyo.identify(identity)).catch((error) => {
          console.error('[wefunder-landing] Klaviyo attribution failed.', error);
        });
        return;
      }

      window._learnq = window._learnq || [];
      window._learnq.push(['identify', identity]);
    }
  }

  customElements.define('wefunder-landing', WefunderLanding);
})();
